import { formatCurrency, formatDate, formatNumber, formatPercent, getLocaleCurrencySymbol } from '@angular/common';
import moment from 'moment';
import { Moment } from 'moment';
import {
    ComparisonOperator,
    HttpParams as HttpParamsInterface,
    IdItem,
    Logo,
    LookupItem,
    NumericLookupItem,
    Quarter,
    SavedReport,
    SegmentItem,
    StringLookupItem,
} from '@models/lookup';
import { Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { shareReplay } from 'rxjs/operators';
import { Segmentation, UiConfig } from '@models/config';
import { round } from 'lodash';
import { CONFIRM_DIALOG, ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { CellClassParams } from 'ag-grid-community';
import {
    AutocompleteFilter,
    DeferrableFilter,
    Filter,
    FilterTypes,
    GroupMultiSelectFilter,
    GroupSelectFilter,
    MultiSelectFilter,
    PoliticalWindowConfigTiersFilter,
    SelectFilter,
} from '@models/filter-types';
import { NotificationService, NotificationType } from '@services/notification/notification.service';
import { RateCardMetricRowV2, RateCardMetricValueV2, RateCardTier } from '@models/rate-card/rate-card';
import { MatDialog } from '@angular/material/dialog';
import { InventoryInsightsFilters } from '@models/inventory-insights/inventory-insights-filters';
import { RateGeneratorFiltersV2 } from '@models/rate-generator/rate-generator-filters-v2';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';

const LOCALE_ID = 'en-US';
const SUNDAY = 7;
const MONDAY = 1;

export const RA_COLORS = {
    black: '#05053A',
    borderColor: '#bbbbbb',
    fadedBorderColor: '#cccccc',
    lightBackground: '#ffffff',
    darkerBackground: '#e6e6eb',
    darkestBackground: '#f0f0f0',
    raPrimaryBlue: '#101D91',
    raFadedBlue: '#cfd2e9',
    raSemiFadedBlue: '#888ec8',
    raSupportGray: '#44446b',
    raLightestGray: '#e6e6eb',
    raDefaultTextGray: '#44446b',
    raTextWhite: '#ffffff',
    raLightBlue: '#a9c3d5',
    raLightCyan: '#b9ffff',
    raLightPurple: '#9c90c2',
    raPrimaryRed: '#B70F0A',
    raSemiFadedRed: '#db8785',
    brightRed: '#ff0000',
    raLightOrange: '#f3af88',
    raPrimaryGreen: '#72E2AF',
    raSemiFadedGreen: '#b9f1d7',
    raSecondaryGreen: '#629C44',
    raSecondaryRed: '#B70F0A',
    raPrimaryOrange: '#ff6f2c',
    link: '#101D91',
    primary: '#101D91',
    success: '#72E2AF',
    danger: '#B70F0A',
    warning: '#ffdd57',
    info: '#101D91',
    text: '#05053A',
    overriddenGrey: '#44446b',
    lowerOverrideRed: '#B70F0A',
    higherOverrideGreen: '#629C44',
    selloutLowerLimitBackground: '#DDFFDD',
    selloutUpperLimitBackground: '#FEE8E9',
    raFadedRed: '#F2D9D5',
    raClosedOutRate: '#5C5C7F',
};

export const LAST_DATA_UPDATE = 'Last Data Update:';

export function divide(numerator: number, denominator: number) {
    if (numerator === 0 || denominator === 0) {
        return 0;
    }

    return numerator / denominator;
}

export function currencyFormatter(value: number, digits: string = '1.2-2'): string {
    const symbol = getLocaleCurrencySymbol(LOCALE_ID) ?? '$';
    return formatCurrency(value, LOCALE_ID, symbol, undefined, digits);
}

export function percentFormatter(value: number, digits: string = '1.0-1'): string {
    return formatPercent(value, LOCALE_ID, digits);
}

export function numberFormatter(value: number, digits: string = '1.0-0'): string {
    return formatNumber(value, LOCALE_ID, digits);
}

export function dateFormatter(value: Date | string | number, format: string = 'shortDate'): string {
    return formatDate(value, format, LOCALE_ID);
}

function isLookupItem(item: LookupItem | NumericLookupItem | StringLookupItem): item is LookupItem {
    return hasProperty(item, 'name');
}

function isStringLookupItem(item: LookupItem | NumericLookupItem | StringLookupItem): item is StringLookupItem {
    return typeof item.id === 'string' && hasProperty(item, 'name');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isMultiSelectFilter(filter: Filter<any>): filter is MultiSelectFilter<any> {
    return filter?.Type === FilterTypes.MultiSelectFilterType;
}

export function isSelectFilter(filter: Filter<unknown>): filter is SelectFilter<unknown> {
    return filter?.Type === FilterTypes.SelectFilterType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isGroupMultiSelectFilter(filter: Filter<any>): filter is GroupMultiSelectFilter<any> {
    return filter?.Type === FilterTypes.GroupMultiSelectFilterType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isGroupSelectFilter(filter: Filter<any>): filter is GroupSelectFilter<any> {
    return filter?.Type === FilterTypes.GroupSelectFilterType;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function isAutocompleteFilter(filter: Filter<unknown>): filter is AutocompleteFilter<unknown> {
    return filter?.Type === FilterTypes.AutocompleteFilterType;
}

export function isPoliticalWindowConfigTiersFilter(filter: Filter<unknown>): filter is PoliticalWindowConfigTiersFilter<unknown> {
    return filter?.Type === FilterTypes.PoliticalWindowConfigTiersFilterType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDeferrableFilter(filter: Filter<any>): filter is DeferrableFilter<unknown> {
    return (
        isSelectFilter(filter) ||
        isMultiSelectFilter(filter) ||
        isPoliticalWindowConfigTiersFilter(filter) ||
        isGroupSelectFilter(filter) ||
        isGroupMultiSelectFilter(filter)
    );
}

export function getDisplayText(item: LookupItem | NumericLookupItem | StringLookupItem | string | number): string | undefined | null {
    if (item || item === '') {
        if (typeof item === 'string' || typeof item === 'number') {
            return String(item);
        }
        if (isStringLookupItem(item)) {
            return item.displayName ?? item.name;
        } else if (isLookupItem(item)) {
            return item.adminDisplayName ?? item.displayName ?? item.name;
        } else {
            return item.displayValue ?? item.value?.toString();
        }
    }
    return null;
}

export function getAllDisplayText(
    items: (LookupItem | NumericLookupItem | StringLookupItem | string | number)[],
): (string | undefined | null)[] {
    return items.map((item) => getDisplayText(item));
}

export function computeFlightWeeks(start: Moment, end: Moment): string[] {
    const startDate = moment(start).day(start.day() >= MONDAY ? MONDAY : MONDAY - 7);
    const lastDay = moment(end).add(SUNDAY - end.isoWeekday(), 'days');
    const items: string[] = [];

    while (startDate < lastDay) {
        items.push(dateFormatter(startDate.toDate(), 'MM/dd/yy'));
        startDate.add(1, 'week');
    }
    return items;
}

export function isMultiOptionSelected<T extends IdItem = LookupItem>(item: T, selectedList: T[]): boolean {
    return selectedList.filter((listItem) => listItem.id === item.id).length !== 0;
}

export function isSingleOptionSelected(
    item: LookupItem | NumericLookupItem | SegmentItem | string,
    selected: LookupItem | NumericLookupItem | SegmentItem | string,
): boolean {
    return item && selected && typeof item !== 'string' && typeof selected !== 'string' ? item.id === selected.id : item === selected;
}

export function doListsHaveSameElements<T extends string | number>(list1: T | T[], list2: T | T[]): boolean {
    const arr1: T[] = Array.isArray(list1) ? list1 : [list1];
    const arr2: T[] = Array.isArray(list2) ? list2 : [list2];

    return arr1.filter((x) => !arr2.includes(x)).length + arr2.filter((x) => !arr1.includes(x)).length === 0;
}

export function isSegmentItemSelected(item: SegmentItem, selected: SegmentItem): boolean {
    return item && selected ? item.id === selected.id : item === selected;
}

export function removeCommasFromValue(val: string | number): string {
    if (val) {
        return val.toString().replace(/,/g, '');
    }
    return String(val);
}

export function generateCachedGet<T>(
    clearCache: boolean,
    url: string,
    http: HttpClient,
    params: HttpParamsInterface,
    cache: { [url: string]: Observable<unknown> } = {},
    useRequestData: boolean = true,
): Observable<T> {
    if (params) {
        if (useRequestData) {
            url = url.concat('?', new HttpParams().set('requestData', JSON.stringify(params)).toString());
        } else {
            Object.keys(params).forEach((key, index) => {
                url =
                    url +
                    (index === 0 ? '?' : '&') +
                    key +
                    '=' +
                    (Array.isArray(params[key]) ? (params[key] as unknown[]).join(',') : params[key]);
            });
        }
    }
    if (clearCache) {
        cache[url] = null;
    }

    if (!cache[url]) {
        cache[url] = http.get<LookupItem[]>(url).pipe(shareReplay(1));
    }
    // @ts-expect-error TS7053: implicit any
    return cache[url];
}

export function downloadFile(data: string, fileName: string) {
    const blob: Blob = new Blob([data], { type: 'text/xml' });

    const a = document.createElement('a');
    document.body.appendChild(a);

    a.style.display = 'none';

    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
}

export function exceedsThreshold(value1: string | number, comparator: ComparisonOperator, value2: string | number) {
    switch (comparator) {
        case '<':
            return value1 < value2;
        case '<=':
            return value1 <= value2;
        case '=':
            return value1 === value2;
        case '>=':
            return value1 >= value2;
        case '>':
            return value1 > value2;
    }
    return false;
}

export function appendSegmentationRequestParams(filters: unknown, body: unknown, segmentations?: Segmentation[]): void {
    if (typeof filters !== 'object' || filters === null || typeof body !== 'object' || body === null) {
        console.warn('Invalid filters or body passed');
        return;
    }
    let channelSegmentation;
    if (segmentations) {
        channelSegmentation = segmentations.map((segmentation) => segmentation.segment_type);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtersObj = filters as Record<string, Array<{ ids: any }>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bodyObj = body as Record<string, any>;
    channelSegmentation = segmentations?.map((segmentation) => segmentation.segment_type);

    Object.keys(filtersObj).forEach((key) => {
        const filterValue = filtersObj[key];
        if (!Array.isArray(filterValue) || filterValue.length === 0) {
            return;
        }

        const firstItem = filterValue[0];
        const isValid = firstItem && typeof firstItem === 'object' && 'ids' in firstItem;

        if ((channelSegmentation && channelSegmentation.includes(key) && isValid) || (!channelSegmentation && isValid)) {
            bodyObj[key] = filterValue.flatMap((item) => item.ids);
        }
    });
}

export function bigMoneyFormatter(
    input: number,
    formatString: string = '1.2-2',
    pipeType: 'number' | 'currency' = 'currency',
    useFullString: boolean = false,
): string | null {
    const pipeOptions = {
        // eslint-disable-next-line id-blacklist
        number: numberFormatter,
        currency: currencyFormatter,
    };
    const suffixes = ['k', 'M', 'B', 'T', 'P', 'E'];
    const fullStrings = ['Thousand', 'Million', 'Billion', 'Trillion', 'Quadrillion', '?'];

    if (input == null || Number.isNaN(input)) {
        return null;
    }

    if (Math.abs(input) < 1000) {
        return pipeOptions[pipeType](input, formatString);
    }

    const exp = Math.floor(Math.floor(Math.log10(Math.abs(input))) / Math.log10(1000));
    const scaledValue = input / Math.pow(1000, exp);

    const suffix = useFullString ? fullStrings[exp - 1] : suffixes[exp - 1];

    return pipeOptions[pipeType](scaledValue, formatString) + suffix;
}

export function segmentTrack(
    eventName: string,
    params: {
        [key: string]: unknown;
    },
): void {
    const rawConfig = localStorage.getItem('userConfig');
    const config = rawConfig ? (JSON.parse(rawConfig) as UiConfig) : null;
    const customerIdDict = { groupId: config?.customerId ?? 'unknown' };
    window.analytics.track(eventName, params, customerIdDict);
}

export function segmentButtonTrack(
    buttonName: string,
    params: {
        [key: string]: string;
    } = {},
): void {
    params.email = localStorage.getItem('email') ?? '';
    params.button_name = buttonName;
    segmentTrack('User Clicked Button', params);
}

export function segmentPage(): void {
    const rawConfig = localStorage.getItem('userConfig');
    const config = rawConfig ? (JSON.parse(rawConfig) as UiConfig) : null;

    window.analytics.page(config?.customerId);
}

export function getBroadcastYearStart(date: Moment) {
    return moment({ year: date.year(), month: 0, day: 1 }).startOf('week').add(1, 'day');
}

export function getBroadcastYearEnd(date: Moment) {
    return moment({ year: date.year() + 1, month: 0, day: 1 }).startOf('week');
}

export function getLogos(resp: { [logoName: string]: Blob }): {
    [logoName: string]: Logo;
} {
    const logos: {
        [logoName: string]: Logo;
    } = {};
    Object.keys(resp).forEach((img) => {
        const url = resp[img];
        const reader = new FileReader();
        reader.onloadend = () => {
            logos[img] = { image: reader.result as string };
            const htmlImage = document.createElement('img');
            htmlImage.src = reader.result as string;
            htmlImage.onload = () => {
                logos[img].height = htmlImage.height;
                logos[img].width = htmlImage.width;
            };
        };
        reader.readAsDataURL(url);
    });
    return logos;
}

export function roundValue(value: number, rounder: number): number {
    return round(value / rounder) * rounder;
}

export function isClosedOutRate(params: CellClassParams): boolean {
    const rules = params?.colDef?.cellClassRules;
    if (rules && hasProperty(rules, 'closed-out-rate')) {
        return (rules['closed-out-rate'] as (params: CellClassParams) => boolean)(params);
    }
    return false;
}

export function openDeleteDaypartOptionDialog(
    option: unknown,
    matDialog: MatDialog,
    snackBar: NotificationService,
    lookupSvc: LookupV2Service,
    filters: InventoryInsightsFilters | RateGeneratorFiltersV2,
): void {
    const confirmDeleteUser = matDialog.open(ConfirmDialogComponent, {
        data: {
            text:
                'Are you sure you want \n to delete this daypart ?\n' +
                // @ts-expect-error TS7053: implicit any
                `${option.name}`,
            autoConfirm: false,
            noClickClose: false,
            confirmText: 'Remove custom daypart',
            denyText: 'Cancel',
        },
    });
    confirmDeleteUser.afterClosed().subscribe((deleteDaypart) => {
        if (deleteDaypart === CONFIRM_DIALOG) {
            // @ts-expect-error TS7053: implicit any
            lookupSvc.deleteCustomDaypartAccessForUser(option.id).subscribe(
                () => {
                    filters.dayparts.options.forEach((daypart, index) => {
                        // @ts-expect-error TS7053: implicit any
                        if (daypart.id === option.id) {
                            filters.dayparts.options.splice(index, 1);
                            filters.dayparts.Options([...filters.dayparts.options]);
                        }
                    });
                    snackBar.openSnackBar('Deleted custom daypart', 'success');
                },
                (error) => {
                    console.error(error);
                },
            );
        }
    });
}

export function nonNullFilterValue(filter: SelectFilter<RateCardTier> | SelectFilter<Quarter>) {
    return filter.Value !== null && filter.Value !== undefined;
}

export function hasProperty(object: object | null | undefined, field: string | number): boolean {
    return Object.hasOwn(object || {}, field);
}

export function handleCustomErrorCode(
    snackBar: (message: string, error: NotificationType) => void,
    error: HttpErrorResponse,
    elseMessage: string | null = null,
) {
    // 403 is our custom error code message from the API
    let message;
    if (error?.status === 403) {
        message = error?.error?.message;
    } else {
        message = elseMessage;
    }
    if (message) {
        snackBar(message, 'error');
    }
    console.error(error);
}

export function isUserRaUser(): boolean {
    const email = localStorage.getItem('email');
    return !!email && (email?.includes('@rateoptics.io') || email?.includes('@revenueanalytics.com'));
}

export function filterRowsToInsert<T extends RateCardMetricValueV2 = RateCardMetricValueV2>(
    rows: RateCardMetricRowV2<T>[],
    currentSelectedRow: RateCardMetricRowV2<T>,
): { insertRows: RateCardMetricRowV2<T>[]; extraRows: RateCardMetricRowV2<T>[] } {
    const insertRows: RateCardMetricRowV2<T>[] = [];
    const extraRows: RateCardMetricRowV2<T>[] = [];

    for (const newRow of rows) {
        if (currentSelectedRow.channelId === newRow.channelId && currentSelectedRow.daypartId === newRow.daypartId) {
            insertRows.push(newRow);
        } else {
            extraRows.push(newRow);
        }
    }
    return { insertRows, extraRows };
}

export function insertExtraRows<T extends RateCardMetricValueV2 = RateCardMetricValueV2>(
    rows: RateCardMetricRowV2<T>[],
    rowData: RateCardMetricRowV2<T>[],
): RateCardMetricRowV2<T>[] {
    for (const newRow of rows) {
        // Insert ABOVE matching channelId + daypartId
        const insertIndex = rowData.findIndex((r) => r.channelId === newRow.channelId && r.daypartId === newRow.daypartId);
        if (insertIndex !== -1) {
            rowData.splice(insertIndex, 0, newRow);
        }
    }
    return rowData;
}

export function sortRateCardData(data: RateCardMetricRowV2[], marketSortOrder: number[], stationSortOrder: number[]) {
    const marketOrderMap = new Map<number, number>();
    marketSortOrder.forEach((id, index) => marketOrderMap.set(id, index));

    const stationOrderMap = new Map<number, number>();
    stationSortOrder.forEach((id, index) => stationOrderMap.set(id, index));

    return data.sort((a, b) => {
        const marketA = marketOrderMap.get(a.marketId) ?? -1;
        const marketB = marketOrderMap.get(b.marketId) ?? -1;

        if (marketA !== marketB) {
            return marketA - marketB;
        }

        const stationA = stationOrderMap.get(a.stationId) ?? -1;
        const stationB = stationOrderMap.get(b.stationId) ?? -1;

        if (stationA !== stationB) {
            return stationA - stationB;
        }

        if (a.station !== b.station) {
            return a.station?.localeCompare(b.station ?? '') ?? 0;
        }

        if (a.channelSortOrder !== b.channelSortOrder) {
            return (a.channelSortOrder ?? 0) - (b.channelSortOrder ?? 0);
        }

        if (a.channel !== b.channel) {
            return a.channel?.localeCompare(b.channel ?? '') ?? 0;
        }

        return 0;
    });
}

export function isConfiguredEmailReport(report: SavedReport): boolean {
    return !!(report?.emailReports?.frequencyId && report?.emailReports?.isActive);
}

export function isEndDatePassed(endDate: moment.MomentInput): boolean {
    if (!endDate) {
        return false;
    }
    const givenDate = moment(endDate, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    return givenDate.isBefore(today);
}
